import React, { useState, useEffect, useMemo } from 'react';
import { Form, Row, Col, Button, Spinner, Card, CardBody } from 'react-bootstrap';
import { updateClubManagerCredentials } from '../../../api/club/clubDetailsService';

interface LoginCredentialsProps {
  clubId: string;
  initialEmail: string;
  isViewMode?: boolean;
}

interface CredentialsData {
  emailId: string;
  password: string;
  confirmPassword: string;
}

const LoginCredentials: React.FC<LoginCredentialsProps> = ({ 
  clubId, 
  initialEmail, 
  isViewMode = false 
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState<CredentialsData>({
    emailId: initialEmail,
    password: '',
    confirmPassword: ''
  });

  // Initialize form data when initialEmail changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      emailId: initialEmail
    }));
  }, [initialEmail]);

  // Mark fields as touched when in edit mode
  useEffect(() => {
    if (isEditMode) {
      setTouchedFields(new Set(['emailId'])); // Only mark emailId as touched, not password fields
    }
  }, [isEditMode]);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Mark field as touched
    setTouchedFields(prev => new Set(prev).add(name));
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateField = (name: string, value: string): string => {
    const trimmedValue = value.trim();
    
    // Check for empty spaces only
    if (trimmedValue.length === 0 && value.length > 0) {
      return 'This field cannot contain only spaces';
    }
    
    // Field-specific validations
    switch (name) {
      case 'emailId':
        if (trimmedValue.length === 0) {
          return 'Email ID is required';
        }
        if ((touchedFields.has(name) || isEditMode) && !validateEmail(trimmedValue)) {
          return 'Please enter a valid email address';
        }
        break;
        
      case 'password':
        // Only show "required" error if field has been touched
        if (touchedFields.has(name) && trimmedValue.length === 0) {
          return 'Password is required';
        }
        // Only validate format if there's content and field is touched or in edit mode
        if (trimmedValue.length > 0 && (touchedFields.has(name) || isEditMode) && trimmedValue.length < 8) {
          return 'Password must be at least 8 characters long';
        }
        break;
        
      case 'confirmPassword':
        // Only show "required" error if field has been touched
        if (touchedFields.has(name) && trimmedValue.length === 0) {
          return 'Please confirm your password';
        }
        // Only validate match if there's content and field is touched or in edit mode
        if (trimmedValue.length > 0 && (touchedFields.has(name) || isEditMode) && formData.password && trimmedValue !== formData.password) {
          return 'Passwords do not match. Please check both password fields.';
        }
        break;
    }
    
    return '';
  };

  // Check if passwords match
  const passwordsMatch = useMemo(() => {
    return formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  }, [formData.password, formData.confirmPassword]);

  // Check if form is valid
  const isFormValid = useMemo(() => {
    const validations = {
      emailId: validateField('emailId', formData.emailId),
      password: validateField('password', formData.password),
      confirmPassword: validateField('confirmPassword', formData.confirmPassword)
    };
    
    const hasErrors = Object.values(validations).some(error => error !== '');
    return !hasErrors;
  }, [formData, touchedFields, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isUpdating) return;
    
    setIsUpdating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate form
      if (!isFormValid) {
        throw new Error('Please fill all required fields correctly');
      }

      // Validate password match
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match. Please ensure both password fields are identical.');
      }

      // Additional validation to ensure passwords are not empty
      if (!formData.password || !formData.confirmPassword) {
        throw new Error('Both password fields are required');
      }

      // Validate email
      if (!formData.emailId?.trim()) {
        throw new Error('Email ID is required');
      }

      // Call API to update credentials
      await updateClubManagerCredentials(clubId, {
        emailId: formData.emailId,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });

      setSuccess('Login credentials updated successfully!');
      setIsEditMode(false);
      
      // Clear password fields after successful update
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
      
    } catch (error: any) {
      setError(error.message || 'Failed to update login credentials');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setError(null);
    setSuccess(null);
    // Reset form data to initial values
    setFormData({
      emailId: initialEmail,
      password: '',
      confirmPassword: ''
    });
    setTouchedFields(new Set());
  };

  return (
    <Card className="login-credentials-card mb-4">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="card-title-section">
            <h4 className="mb-0">Login Credentials</h4>
          </div>
          {!isViewMode && !isEditMode && (
            <Button 
              variant="dark" 
              size="sm"
              onClick={() => setIsEditMode(true)}
              title="Edit Login Credentials"
            >
              <i className="ri-edit-line me-1"></i>
              Edit
            </Button>
          )}
        </div>

        <Form onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div className="mb-3">
              <div className="alert alert-danger">
                <i className="ri-error-warning-line me-2"></i>
                {error}
              </div>
            </div>
          )}

          {/* Success Display */}
          {success && (
            <div className="mb-3">
              <div className="alert alert-success">
                <i className="ri-check-line me-2"></i>
                {success}
              </div>
            </div>
          )}

          <div className="password-requirements mb-3">
            <small className="text-muted">
              Password must be at least 8 characters, include 1 uppercase, 1 number, and 1 special character.
            </small>
          </div>

          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="form-label">
                  Email ID <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="emailId"
                  placeholder="Enter Email ID"
                  value={formData.emailId}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                  className={`form-control ${(touchedFields.has('emailId') || isEditMode) && validateField('emailId', formData.emailId) ? 'is-invalid' : ''}`}
                />
                {(touchedFields.has('emailId') || isEditMode) && validateField('emailId', formData.emailId) && (
                  <Form.Control.Feedback type="invalid">
                    {validateField('emailId', formData.emailId)}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="form-label">
                  Password <span className="text-danger">*</span>
                </Form.Label>
                <div className="password-input-wrapper">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter new password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    className={`form-control ${(touchedFields.has('password') || isEditMode) && validateField('password', formData.password) ? 'is-invalid' : ''}`}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`ri-eye-${showPassword ? 'line' : 'off-line'}`}></i>
                  </button>
                </div>
                {(touchedFields.has('password') || isEditMode) && validateField('password', formData.password) && (
                  <Form.Control.Feedback type="invalid">
                    {validateField('password', formData.password)}
                  </Form.Control.Feedback>
                )}
                {(touchedFields.has('password') || isEditMode) && formData.password && !validateField('password', formData.password) && (
                  <div className="text-success small mt-1">
                    <i className="ri-check-line me-1"></i>
                    Password meets requirements
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="form-label">
                  Confirm Password <span className="text-danger">*</span>
                </Form.Label>
                <div className="password-input-wrapper">
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Re-enter password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    className={`form-control ${(touchedFields.has('confirmPassword') || isEditMode) && validateField('confirmPassword', formData.confirmPassword) ? 'is-invalid' : ''}`}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <i className={`ri-eye-${showConfirmPassword ? 'line' : 'off-line'}`}></i>
                  </button>
                </div>
                {(touchedFields.has('confirmPassword') || isEditMode) && validateField('confirmPassword', formData.confirmPassword) && (
                  <Form.Control.Feedback type="invalid">
                    {validateField('confirmPassword', formData.confirmPassword)}
                  </Form.Control.Feedback>
                )}
                {formData.confirmPassword && !validateField('confirmPassword', formData.confirmPassword) && passwordsMatch && (
                  <div className="text-success small mt-1">
                    <i className="ri-check-line me-1"></i>
                    Passwords match
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Action Buttons */}
          {isEditMode && (
            <div className="action-buttons-section mt-4">
              <Button
                type="button"
                variant="outline-secondary"
                className="me-2"
                onClick={handleCancel}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isUpdating || !isFormValid}
              >
                {isUpdating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Update Credentials'
                )}
              </Button>
            </div>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

export default LoginCredentials; 